import { useEffect, useState } from 'react';
import { Box, MenuItem, Slider, Typography } from '@mui/material';
import TextField from 'src/components/TextField';
import SwitchButton from 'src/components/Switch/Switch';
import { HMIField } from '../../types';
import ArrayGridField from './ArrayGridField';

export interface ControlFieldProps {
  field: HMIField;
  value: unknown;
  disabled?: boolean;
  onCommit: (value: unknown) => void;
}

/** Numeric controls render as a slider when the schema defines a bounded range + increment (matches BMT's Preform Rotation / Fan Speed sliders); otherwise a plain numeric box. */
function isSliderCandidate(field: HMIField): boolean {
  return (
    (field.datatype === 'i32' || field.datatype === 'f32' || field.datatype === 'f64') &&
    typeof field.rangeMin === 'number' &&
    typeof field.rangeMax === 'number' &&
    typeof field.increment === 'number' &&
    field.increment > 0
  );
}

function ControlField({ field, value, disabled, onCommit }: ControlFieldProps) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  if (field.datatype === 'array') {
    return (
      <ArrayGridField
        field={field}
        value={Array.isArray(value) ? (value as number[]) : []}
        disabled={disabled}
        onCommit={onCommit}
      />
    );
  }

  if (field.datatype === 'bool') {
    return (
      <SwitchButton
        value={Boolean(value)}
        disabled={disabled}
        onChange={(e) => onCommit((e.target as unknown as { value: boolean }).value)}
      />
    );
  }

  if (field.datatype === 'enum') {
    return (
      <TextField
        select
        size="small"
        value={value ?? ''}
        disabled={disabled}
        sx={{ minWidth: 160 }}
        onChange={(e) => onCommit(e.target.value)}
      >
        {(field.options ?? []).map((opt) => (
          <MenuItem key={String(opt.value)} value={opt.value as string}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (isSliderCandidate(field)) {
    return (
      <Box sx={{ width: 220, px: 1 }}>
        <Slider
          size="small"
          disabled={disabled}
          value={typeof draft === 'number' ? draft : (field.rangeMin as number)}
          min={field.rangeMin}
          max={field.rangeMax}
          step={field.increment}
          valueLabelDisplay="auto"
          marks={[
            { value: field.rangeMin as number, label: String(field.rangeMin) },
            { value: field.rangeMax as number, label: String(field.rangeMax) },
          ]}
          onChange={(_, v) => setDraft(v as number)}
          onChangeCommitted={(_, v) => onCommit(v as number)}
        />
      </Box>
    );
  }

  if (field.datatype === 'string') {
    return (
      <TextField
        size="small"
        value={draft ?? ''}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== value && onCommit(draft)}
      />
    );
  }

  // Plain numeric input (i32 / f32 / f64 without slider hints)
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <TextField
        size="small"
        type="number"
        value={draft ?? ''}
        disabled={disabled}
        inputProps={{
          min: field.rangeMin,
          max: field.rangeMax,
          step: field.increment ?? (field.decimals ? 1 / 10 ** field.decimals : 1),
        }}
        sx={{ width: 110 }}
        onChange={(e) => setDraft(e.target.value === '' ? '' : Number(e.target.value))}
        onBlur={() => draft !== value && draft !== '' && onCommit(Number(draft))}
      />
      {field.unit && (
        <Typography variant="caption" color="text.secondary">
          {field.unit}
        </Typography>
      )}
    </Box>
  );
}

export default ControlField;
